<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import IExtraService from '@components/IExtraService/IExtraService.vue';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';

translator.getLanguages();

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  inactive: {
    type: Boolean,
    default: false,
  },
  short: {
    type: Boolean,
    default: false,
  },
  allowedLoyalty: {
    type: Array,
    default: null,
  },
  isHistory: {
    type: Boolean,
    default: false,
  },
  orderId: {
    type: Number,
    default: 0,
  },
});
const emits = defineEmits(['removeTicket', 'changeLoyalty']);

const isShowDropDownLoyalty = ref(false);
const allowedLoyalty = computed(() => {
  if (props.allowedLoyalty === null) return null;
  return [{ id: 0, categoryId: 0, name: 'Основной' }, ...props.allowedLoyalty];
});
const currentLoyalty = computed(() => {
  if (props.ticket.loyaltyCategoryId === null) return { id: 0, categoryId: 0, name: 'Основной' };
  return allowedLoyalty.value.find((item) => item.id === props.ticket.loyaltyCategoryId);
});

const sectorName = computed(() => (props.ticket.sector.includes('Сектор') ? props.ticket.sector : `Сектор ${props.ticket.sector}`));

function toggleDropDownLoyalty() {
  if (props.isHistory) {
    return;
  }

  isShowDropDownLoyalty.value = !isShowDropDownLoyalty.value;
}

const loyaltyBtn = ref(null);
function clickOutsideDropdown(e) {
  if (e.target === loyaltyBtn.value || e.target.parentElement === loyaltyBtn.value) {
    return;
  }

  isShowDropDownLoyalty.value = false;
}

function changeLoyalty(categoryLoyalty) {
  emits(
    'changeLoyalty',
    {
      ticket: props.ticket,
      categoryId: categoryLoyalty.id,
      priceId: categoryLoyalty.categoryId ?? categoryLoyalty.priceId,
    },
  );

  isShowDropDownLoyalty.value = false;
}
</script>

<template>
  <div
    class="i-ticket large"
    :class="{
      inactive, short,
      'i-ticket__refunded': isHistory && ticket.isRefunded
    }"
  >
    <div class="i-ticket__content">
      <div class="i-ticket__content-top">
        <template v-if="ticket.sector">
          <div class="i-ticket__sector">{{ sectorName }}</div>
          <div class="i-ticket__place">Ряд {{ ticket.row }}, Место {{ ticket.col }}</div>
        </template>
        <template v-else-if="ticket.place">
          {{ ticket.place }}
        </template>
        <template v-else>
          {{ ticket.type }}
        </template>
        <IExtraService
          v-if="
            ticket?.additionalServices
              && (ticket?.additionalServices?.length > 0 || ticket?.additionalTickets?.length > 0)"
          :additional-services="ticket?.additionalServices ?? []"
          :additional-tickets="ticket?.additionalTickets ?? []"
          :order-id="orderId"
        />
      </div>
      <div class="i-ticket__bottom">
        <div class="i-ticket__bottom-wrapper">
          <div class="i-ticket__place-price">{{ ticket.price }} <span class="ruble">₽</span></div>
          <div v-if="props.allowedLoyalty?.length" class="i-ticket__loyalty-wrapper">
            <div
              ref="loyaltyBtn"
              class="dropdown"
              :disabled="props.inactive"
              :class="{ active: isShowDropDownLoyalty, dropdown_inactive: isHistory }"
              @click="toggleDropDownLoyalty"
            >
              <svg class="dropdown__type-svg" height="20" width="20">
                <use
                  v-if="currentLoyalty.id === 0"
                  xlink:href="#ticket-category"
                />
                <use
                  v-else-if="currentLoyalty.id === 1"
                  xlink:href="#user-child"
                />
                <use
                  v-else
                  xlink:href="#other-preferential-category"
                />
              </svg>
              <div class="dropdown__title">{{ currentLoyalty.name }}</div>
              <svg
                v-if="!props.inactive && !isHistory"
                class="dropdown__icon-down"
                height="16"
                width="16"
              >
                <use xlink:href="#down" />
              </svg>
            </div>
            <div
              v-if="isShowDropDownLoyalty"
              v-on-click-outside="clickOutsideDropdown" class="dropdown__list"
            >
              <div
                v-for="categoryLoyalty in allowedLoyalty"
                :key="categoryLoyalty.id"
                class="dropdown__item"
                @click="changeLoyalty(categoryLoyalty)"
              >
                <input
                  v-model="currentLoyalty.id"
                  class="mr-2"
                  type="radio"
                  :value="categoryLoyalty.id"
                >
                <svg height="20" width="17">
                  <use
                    v-if="categoryLoyalty.id === 0"
                    xlink:href="#ticket-category"
                  />
                  <use
                    v-else-if="categoryLoyalty.id === 1"
                    xlink:href="#user-child"
                  />
                  <use
                    v-else
                    xlink:href="#other-preferential-category"
                  />
                </svg>
                {{ categoryLoyalty.name }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <button
      v-if="!isHistory"
      class="i-ticket__remove-button reset-btn"
      type="button"
      title="Удалить"
      @click="$emit('removeTicket', props.ticket)"
    >
      <svg height="15" width="15">
        <use xlink:href="#close" />
      </svg>
    </button>
  </div>
  <slot>
    <div />
  </slot>
</template>

<style scoped lang="scss">
.i-ticket {
  position: relative;
  min-height: 112px;
  min-width: 180px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  color: $primary;

  &.large {
    min-width: 224px;

    .i-ticket {
      &__content {
        padding: 18px 15px;
      }
    }
  }

  &.short {
    min-height: auto;
    min-width: auto;
    text-align: center;

    .i-ticket {
      &__content {
        font-size: 14px;
        padding: 8px 20px;
        align-items: center;

        &-top {
          flex-direction: row;
        }
      }

      &__sector,
      &__place {
        margin-bottom: 4px;
        display: inline-block;
        font-weight: 400;
      }

      &__sector::after {
        content: ",\00A0";
      }

      &__place-price {
        text-align: center;
      }
    }
  }

  &.inactive {
    .i-ticket {
      &__content {
        border-color: $gray-stroke;
      }

      &__remove-button {
        display: none;
      }
    }

    &::after,
    &::before {
      border-color: $gray-stroke;
    }
  }

  &__content {
    width: 100%;
    height: 100%;
    min-height: 112px;
    padding: 18px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    flex-direction: column;
    font-size: 14px;
    line-height: 130%;
    border-radius: 8px;
    text-align: left;
    background-color: $white;
    border: 2px solid $ticket-color;
    gap: 9px;

    &-top {
      display: flex;
      flex-direction: column;
    }
  }

  &__sector,
  &__place {
    white-space: nowrap;
  }

  &__sector {
    margin-bottom: 9px;
  }

  &__place {
    font-weight: 700;

    &-price {
      font-weight: 700;
      display: block;
    }
  }

  &__bottom {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 6px;
    width: 100%;

    .dropdown {
      width: fit-content;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 5px;

      &_inactive {
        cursor: auto;
        border: none;
        padding-left: 0;
      }

      &__list {
        cursor: default;
        position: absolute;
        left: 0;
        top: 100%;
        background: $white;
        z-index: 1;
        box-shadow: 0px 0px 8px rgba(153, 153, 153, 0.25);
        border-radius: 4px;
        padding: 7px 17px 17px;
      }

      &__item {
        cursor: pointer;
        margin-top: 10px;
        display: flex;
        align-items: center;

        svg {
          margin-right: 6px;
        }

        input {
          cursor: pointer;
          width: 16px;
          height: 16px;
        }
      }

      &__icon-down {
        transition: rotate 0.3s;
        stroke: $primary;
      }

      &.active {
        .dropdown__icon-down {
          rotate: 180deg;
        }
      }

      &[disabled="true"] {
        pointer-events: none;

        .dropdown__icon-down {
          display: none;
        }
      }
    }
  }

  &__loyalty-wrapper {
    position: relative;
    margin-left: 8px;
  }

  &__remove-button {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 15px;
    height: 15px;
    display: inline-flex;
    color: $primary;

    &:hover {
      color: $color;
    }
  }

  &__bottom-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__refunded {
    opacity: .5;
  }

  @media (max-width: $screen-xs) {
    width: 100%;
    max-width: inherit;
    min-height: auto;

    &__content {
      flex-grow: 1;
      text-align: left;
      padding: 20px;
      align-items: flex-start;
      min-height: auto;
    }

    &__remove {
      &-button {
        &:after {
          filter: brightness(100%);
        }
      }
    }

    &__place {
      width: 100%;
    }
  }
}
</style>
